import { Link, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DSDrawer from "views/system-design/DSDrawer";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSTooltip from "views/system-design/DSTooltip";
import firebaseService from "firebase_service/firebaseService";
import {
  APPBAR_HEIGHT,
  DRAWER_WIDTH,
  DRAWER_WIDTH_COLLAPSED,
} from "consts/layout";
import useDeviceType from "components/hooks/useDeviceType";
import { useHasNewReviewItems } from "../hooks/useNewReviewItems";
import {
  useHasNewBriefSubmissions,
  useHasNewArtistsContactSubmissions,
} from "../hooks/useNewBriefSubmissions";
import { useNumberOfCollectionsWaitingForPublish } from "../hooks/useNumberOfCollectionsWaitingForPublish";
import { useHasNewSentryIssues } from "views/SentryIssues/hooks/useNewSentryIssues";
import { useHasUnacknowledgedMonitorFailures } from "views/KlydoMonitor/hooks/useKlydoMonitors";
import { NAV_SECTIONS } from "navigation";

type SidebarProps = {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
};

type NavItemProps = {
  title: string;
  icon: React.ReactNode;
  isSelected?: boolean;
  showNotification?: boolean;
  // Shown as a number badge instead of the dot when above zero.
  notificationCount?: number;
  collapsed: boolean;
  // Route path for navigation items — rendered as a real link so
  // Cmd/Ctrl/Shift+click and middle-click open it in a new browser tab/window.
  href?: string;
  // Side-effect on activation (e.g. closing the mobile drawer, or the action
  // for non-navigation items like Log Out / Collapse).
  onClick?: () => void;
};

const NavItem = ({
  title,
  icon,
  isSelected = false,
  showNotification = false,
  notificationCount = 0,
  collapsed,
  href,
  onClick,
}: NavItemProps) => {
  const { palette } = useTheme();

  const button = (
    <DSBox
      onClick={href ? undefined : onClick}
      role={href ? undefined : "button"}
      tabIndex={href ? undefined : 0}
      aria-current={isSelected ? "page" : undefined}
      onKeyDown={
        href
          ? undefined
          : (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
      }
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        gap: 1.5,
        width: "100%",
        px: collapsed ? 0 : 2,
        py: 1,
        borderRadius: "12px",
        cursor: "pointer",
        userSelect: "none",
        color: isSelected ? palette.surface.accentText : palette.text.secondary,
        backgroundColor: isSelected ? palette.white[8] : "transparent",
        transition: "background-color 0.15s ease, color 0.15s ease",
        "&:hover": {
          backgroundColor: palette.white[8],
          color: isSelected ? palette.surface.accentText : palette.text.primary,
        },
      }}
    >
      <DSBox
        sx={{
          position: "relative",
          display: "flex",
          color: "inherit",
          flexShrink: 0,
        }}
      >
        {icon}
        {notificationCount > 0 && (
          <DSBox
            sx={{
              position: "absolute",
              top: -6,
              right: -8,
              minWidth: 16,
              height: 16,
              px: 0.5,
              borderRadius: "8px",
              backgroundColor: palette.error.main,
              color: palette.common.white,
              fontSize: 10,
              fontWeight: 700,
              lineHeight: "16px",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            {notificationCount}
          </DSBox>
        )}
        {notificationCount === 0 && showNotification && (
          <DSBox
            sx={{
              position: "absolute",
              top: -3,
              right: -3,
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: palette.error.main,
              pointerEvents: "none",
            }}
          />
        )}
      </DSBox>
      {!collapsed && (
        <DSTypography
          variant="subtitle2"
          noWrap
          sx={{ color: "inherit", fontSize: "14px" }}
        >
          {title}
        </DSTypography>
      )}
    </DSBox>
  );

  const item = href ? (
    <Link
      to={href}
      onClick={onClick}
      style={{ textDecoration: "none", display: "block" }}
    >
      {button}
    </Link>
  ) : (
    button
  );

  return collapsed ? (
    <DSTooltip title={title} placement="right">
      {item}
    </DSTooltip>
  ) : (
    item
  );
};

const Sidebar = ({
  open,
  onClose,
  collapsed,
  onToggleCollapsed,
}: SidebarProps) => {
  const location = useLocation();
  const { isSmallLaptop } = useDeviceType();
  const { hasAny } = useHasNewReviewItems();
  const { data: hasNewBriefSubmissions } = useHasNewBriefSubmissions();
  const { data: hasNewArtistsContactSubmissions } =
    useHasNewArtistsContactSubmissions();
  const { data: collectionsWaitingForPublish } =
    useNumberOfCollectionsWaitingForPublish();
  const { data: hasUnacknowledgedMonitorFailures } =
    useHasUnacknowledgedMonitorFailures();
  const { data: hasNewSentryIssues } = useHasNewSentryIssues();
  const { spacing, palette } = useTheme();

  // mobile drawer is never rail-collapsed
  const isRail = collapsed && !isSmallLaptop;

  const isActiveRoute = (routeName: string) =>
    location.pathname.startsWith(routeName);

  // Navigation is handled by the <Link> inside NavItem; this only closes the
  // temporary drawer on mobile after a route is picked.
  const handleNavigate = () => {
    if (isSmallLaptop) onClose();
  };

  return (
    <DSDrawer
      variant={isSmallLaptop ? "temporary" : "permanent"}
      anchor="left"
      open={isSmallLaptop ? open : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        zIndex: (theme) =>
          isSmallLaptop ? theme.zIndex.drawer + 2 : theme.zIndex.drawer,
        "& .MuiDrawer-paper": {
          width: spacing(isRail ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH),
          height: isSmallLaptop
            ? "100%"
            : `calc(100% - ${spacing(APPBAR_HEIGHT)})`,
          marginTop: isSmallLaptop ? 0 : spacing(APPBAR_HEIGHT),
          borderRadius: isSmallLaptop ? "0px" : "0px 28px 0px 0px",
          background: palette.surface.drawer,
          boxShadow:
            "0px 1px 3px 0px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.20)",
          borderRight: "none",
          display: "flex",
          flexDirection: "column",
          px: isRail ? 1 : 2,
          py: 2,
          transition: "width 0.2s ease",
          overflowX: "hidden",
        },
      }}
    >
      <DSBox
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
          pt: isSmallLaptop ? 1 : 0,
        }}
      >
        {NAV_SECTIONS.map((section, sectionIndex) => (
          <DSBox key={section.label} sx={{ mb: 1 }}>
            {isRail ? (
              sectionIndex > 0 && (
                <DSBox
                  sx={{
                    height: "1px",
                    backgroundColor: palette.white[8],
                    mx: 1.5,
                    my: 1,
                  }}
                />
              )
            ) : (
              <DSTypography
                variant="subtitle2"
                sx={{
                  px: 2,
                  pt: sectionIndex === 0 ? 0.5 : 2,
                  pb: 0.75,
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  color: palette.text.disabled,
                }}
              >
                {section.label}
              </DSTypography>
            )}
            <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {section.items.map((route) => {
                const Icon = route.icon;
                return (
                  <NavItem
                    key={route.path}
                    title={route.name}
                    icon={<Icon fontSize="small" />}
                    collapsed={isRail}
                    isSelected={isActiveRoute(route.layout + route.path)}
                    showNotification={
                      (route.path === "/reviews" && hasAny) ||
                      (route.path === "/artists-briefs" &&
                        !!hasNewBriefSubmissions) ||
                      (route.path === "/new-artists" &&
                        !!hasNewArtistsContactSubmissions) ||
                      // Klydo Monitors hosts both the monitors and the Sentry
                      // issues tabs, so either can raise its dot.
                      (route.path === "/klydo-monitors" &&
                        (!!hasUnacknowledgedMonitorFailures ||
                          !!hasNewSentryIssues))
                    }
                    notificationCount={
                      route.path === "/collections"
                        ? collectionsWaitingForPublish
                        : undefined
                    }
                    href={route.layout + route.path}
                    onClick={handleNavigate}
                  />
                );
              })}
            </DSBox>
          </DSBox>
        ))}
      </DSBox>

      <DSBox
        sx={{
          mt: "auto",
          pt: 1,
          borderTop: `1px solid ${palette.white[8]}`,
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
        }}
      >
        <NavItem
          title="Log Out"
          icon={<LogoutOutlinedIcon fontSize="small" />}
          collapsed={isRail}
          onClick={() => firebaseService.logOut()}
        />
        {!isSmallLaptop && (
          <NavItem
            title="Collapse"
            icon={
              isRail ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <ChevronLeftIcon fontSize="small" />
              )
            }
            collapsed={isRail}
            onClick={onToggleCollapsed}
          />
        )}
      </DSBox>
    </DSDrawer>
  );
};

export default Sidebar;
